/**
 * Phosphor Icon Wrappers (Duotone Weight)
 *
 * This module provides React component wrappers around Phosphor icons
 * configured to use the "duotone" weight by default. Each icon is exported
 * as a component that can be passed as `React.ElementType` and is compatible
 * with the existing Lucide API (accepts className).
 *
 * Uses individual CSR imports for better tree-shaking and Vite compat.
 */

import { type ComponentPropsWithoutRef, forwardRef } from "react";

// Individual icon imports (CSR – client-side rendered)
import { House } from "@phosphor-icons/react/House";
import { CalendarBlank } from "@phosphor-icons/react/CalendarBlank";
import { UsersThree } from "@phosphor-icons/react/UsersThree";
import { UserGear } from "@phosphor-icons/react/UserGear";
import { CurrencyDollar } from "@phosphor-icons/react/CurrencyDollar";
import { Church } from "@phosphor-icons/react/Church";
import { Briefcase } from "@phosphor-icons/react/Briefcase";
import { PaperPlaneTilt } from "@phosphor-icons/react/PaperPlaneTilt";
import { ChartBar } from "@phosphor-icons/react/ChartBar";
import { User } from "@phosphor-icons/react/User";
import { GearSix } from "@phosphor-icons/react/GearSix";
import { SquaresFour } from "@phosphor-icons/react/SquaresFour";
import { ShieldCheck } from "@phosphor-icons/react/ShieldCheck";
import { FileText } from "@phosphor-icons/react/FileText";
import { ClipboardText } from "@phosphor-icons/react/ClipboardText";
import { ListChecks } from "@phosphor-icons/react/ListChecks";
import { Plugs } from "@phosphor-icons/react/Plugs";
import { Handshake } from "@phosphor-icons/react/Handshake";
import { Heart } from "@phosphor-icons/react/Heart";
import { BookOpenText } from "@phosphor-icons/react/BookOpenText";
import { CalendarCheck } from "@phosphor-icons/react/CalendarCheck";
import { HandHeart } from "@phosphor-icons/react/HandHeart";
import { ChatCircle } from "@phosphor-icons/react/ChatCircle";
import { EnvelopeSimple } from "@phosphor-icons/react/EnvelopeSimple";
import { Megaphone } from "@phosphor-icons/react/Megaphone";
import { Newspaper } from "@phosphor-icons/react/Newspaper";
import { Users } from "@phosphor-icons/react/Users";
import { Calendar } from "@phosphor-icons/react/Calendar";

type PhosphorIcon = typeof House;
type IconProps = ComponentPropsWithoutRef<"svg"> & { size?: number | string };

/**
 * Creates a Phosphor icon component that defaults to duotone weight.
 * Compatible with Lucide's React.ElementType pattern.
 */
function duotone(PhIcon: PhosphorIcon, displayName: string) {
  const Wrapper = forwardRef<SVGSVGElement, IconProps>(
    ({ className, ...props }, ref) => (
      <PhIcon ref={ref} className={className} weight="duotone" {...props} />
    )
  );
  Wrapper.displayName = displayName;
  return Wrapper;
}

// ──────────────────────────────────────────────────────────────────
// PRIMARY NAVIGATION ICONS (Duotone)
// ──────────────────────────────────────────────────────────────────

export const HomeIcon = duotone(House, "HomeIcon");
export const CalendarIcon = duotone(CalendarBlank, "CalendarIcon");
export const PeopleIcon = duotone(UsersThree, "PeopleIcon");
export const HrIcon = duotone(UserGear, "HrIcon");
export const FinanceIcon = duotone(CurrencyDollar, "FinanceIcon");
export const MinistryIcon = duotone(Church, "MinistryIcon");
export const OperationsIcon = duotone(Briefcase, "OperationsIcon");
export const MessagingIcon = duotone(PaperPlaneTilt, "MessagingIcon");
export const ReportsIcon = duotone(ChartBar, "ReportsIcon");
export const ProfileIcon = duotone(User, "ProfileIcon");
export const SettingsIcon = duotone(GearSix, "SettingsIcon");

// ──────────────────────────────────────────────────────────────────
// SECONDARY / CHILD NAVIGATION ICONS (Duotone)
// ──────────────────────────────────────────────────────────────────

export const DashboardIcon = duotone(SquaresFour, "DashboardIcon");
export const ShieldIcon = duotone(ShieldCheck, "ShieldIcon");
export const DocumentIcon = duotone(FileText, "DocumentIcon");
export const ClipboardIcon = duotone(ClipboardText, "ClipboardIcon");
export const ChecklistIcon = duotone(ListChecks, "ChecklistIcon");
export const IntegrationsIcon = duotone(Plugs, "IntegrationsIcon");
export const HandshakeIcon = duotone(Handshake, "HandshakeIcon");
export const HeartIcon = duotone(Heart, "HeartIcon");
export const BookIcon = duotone(BookOpenText, "BookIcon");
export const CalendarCheckIcon = duotone(CalendarCheck, "CalendarCheckIcon");
export const CareIcon = duotone(HandHeart, "CareIcon");
export const ChatIcon = duotone(ChatCircle, "ChatIcon");
export const MailIcon = duotone(EnvelopeSimple, "MailIcon");
export const MegaphoneIcon = duotone(Megaphone, "MegaphoneIcon");
export const NewsIcon = duotone(Newspaper, "NewsIcon");
export const UsersIcon = duotone(Users, "UsersIcon");
export const CalendarSimpleIcon = duotone(Calendar, "CalendarSimpleIcon");
export const DollarIcon = duotone(CurrencyDollar, "DollarIcon");
